import * as React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Paper, Typography, Skeleton } from '@mui/material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import random from 'lodash/random';
import moment from 'moment';
import { useResponsive } from 'src/hooks/useResponsive';
import { IAction } from 'components/activity/models';
import { ITaxLine } from 'components/basket/model/Basket';
import { ViewBillOrderItem } from 'components/bill/ui/ViewBill/ViewBillOrderItem';
import { locationApi } from 'components/location/locationApi';
import { IOrderReadResourceV18, OrderScenario } from 'components/order/model/Order';
import { getCurrencyString } from 'lib/helpers';
import { LoadingImage } from 'lib/LoadingImage';
import logger from 'lib/logger';
import { Location } from '../../../location/model/Location';
import { orderApi } from 'components/order/orderApi';
import { useSnackbar } from 'notistack';
import { ButtonV2 } from 'ui/ButtonV2';
import { useDeliverySummary } from 'components/delivery/hooks/useDeliverySummary';
import { useAuth } from 'lib/useAuth';
import { DeliverySuccessBox } from 'components/delivery/success/DeliverySuccessBox';
import { DeliveryPinBox } from 'components/delivery/success/DeliveryPinBox';

const PREFIX = 'OrderDetails';

const classes = {
    appBarTitle: `${PREFIX}appBarTitle`,
    cardImage: `${PREFIX}cardImage`,
    cardOrderDetails: `${PREFIX}cardOrderDetails`,
    cardPayments: `${PREFIX}cardPayments`,
    title: `${PREFIX}title`,
    bold: `${PREFIX}bold`,
    button: `${PREFIX}button`
};

const StyledOrderDetails = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    [`& .${classes.cardImage}`]: {
        marginBottom: theme.spacing(2),
        overflow: 'hidden'
    },

    [`& .${classes.cardOrderDetails}`]: {
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(2)
    },

    [`& .${classes.cardPayments}`]: {
        padding: theme.spacing(1, 1.5)
    },

    [`& .${classes.title}`]: {
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        color: theme.palette.titleTextColour
    },

    [`& .${classes.bold}`]: {
        fontWeight: 'bold'
    }
}));

const StyledAppBarContainer = styled(Box, {
    shouldForwardProp: prop => prop !== 'isDesktop'
})<{ isDesktop: boolean }>(({ theme, isDesktop }) => ({
    display: 'flex',
    alignItems: 'center',
    ...(!isDesktop ? { backgroundColor: theme.palette.primary.contrastText } : {}),

    [`&. ${classes.appBarTitle}`]: {
        textAlign: 'center',
        padding: theme.spacing(1.5, 0),
        ...(!isDesktop ? { width: `calc(100% - ${theme.spacing(12)})` } : { width: '100%' }),
        fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
        color: theme.palette.titleTextColour
    }
}));

const StyledAppBarTitle = styled(Typography, {
    shouldForwardProp: prop => prop !== 'isDesktop'
})<{ isDesktop: boolean }>(({ theme, isDesktop }) => ({
    textAlign: 'center',
    padding: theme.spacing(1.5, 0),
    ...(!isDesktop ? { width: `calc(100% - ${theme.spacing(12)})` } : { width: '100%' }),
    fontFamily: `TenantFont, ${theme.typography.fontFamily}`,
    color: theme.palette.titleTextColour
}));

interface IProps {
    action?: IAction | null;
    orderId?: string;
    locationId?: string;
    createdAt?: string;
    onClose: () => void;
    openResendReceipt?: (actionId: string | undefined) => void;
}

export const OrderDetails: React.FC<IProps> = ({
    onClose,
    action,
    openResendReceipt,
    orderId,
    locationId,
    createdAt
}) => {
    const { t } = useTranslation();
    const { isDesktop } = useResponsive();

    const { enqueueSnackbar } = useSnackbar();

    const { user } = useAuth();
    const [location, setLocation] = React.useState<Location | null>(null);
    const [order, setOrder] = React.useState<IOrderReadResourceV18 | null>(null);
    const [orderLoading, setOrderLoading] = React.useState(true);
    const [locationLoading, setLocationLoading] = React.useState(false);

    const fetchOrder = React.useCallback(() => {
        async function getOrderStatus() {
            if (!orderId) {
                return;
            }
            try {
                const result = await orderApi.getOrder(orderId);
                setOrder(result);
            } catch (e) {
                logger.error(e);
                enqueueSnackbar(t('ORDER_DETAILS_REQUEST_ERROR'), { variant: 'error' });
            } finally {
                setOrderLoading(false);
            }
        }
        if (!!user) {
            getOrderStatus();
        }
    }, [enqueueSnackbar, orderId, t, user]);

    React.useEffect(() => {
        fetchOrder();
    }, [fetchOrder]);

    React.useEffect(() => {
        async function getLocation() {
            setLocationLoading(true);
            try {
                if (locationId) {
                    const locationResult = await locationApi.getLocation(locationId);
                    setLocation(locationResult);
                }
            } catch (e) {
                logger.error(e);
            } finally {
                setLocationLoading(false);
            }
        }
        getLocation();
    }, [locationId]);

    const renderItems = React.useCallback(
        (item: any) =>
            order ? <ViewBillOrderItem orderItem={item} allItems={order.items} key={item.id} /> : null,
        [order]
    );

    const renderItemsSkeleton = React.useCallback(
        (_: string, index: any) => (
            <Box key={index} display="flex" width="100%">
                <Skeleton height={24} width="10%" />
                <Box display="flex" width="100%" justifyContent="space-between" paddingLeft={2}>
                    <Skeleton height={24} width={random(100, 200)} />
                    <Skeleton height={24} width={random(40, 60)} />
                </Box>
            </Box>
        ),
        []
    );

    const renderPaymentsSkeleton = React.useCallback(
        (_: string, index: any) => (
            <Box key={index} display="flex" justifyContent="space-between" width="100%">
                <Skeleton height={24} width={random(100, 200)} />
                <Skeleton height={24} width={random(50, 70)} />
            </Box>
        ),
        []
    );

    const handleClickOpenResendReceipt = React.useCallback(() => {
        if (action) {
            openResendReceipt?.(action?._id);
        }
    }, [action, openResendReceipt]);

    const processTaxes = React.useCallback(
        (taxes: ITaxLine[]) =>
            taxes.reduce((acc: number, item: ITaxLine): number => {
                if (!item.inclusive) {
                    return (acc += item.amount);
                }
                return acc;
            }, 0),
        []
    );

    const orderData = React.useMemo(
        () => moment(createdAt).format('dddd Do MMMM YYYY [at] hh:mm A'),
        [createdAt]
    );

    const totalSum = React.useMemo(() => {
        if (order) {
            return order.total.payments !== 0
                ? getCurrencyString(order.total.payments, order.currencyCode)
                : getCurrencyString(order.total.total, order.currencyCode);
        }
    }, [order]);

    const { showDelivery, showPin, trackMyOrderLink, due, statusString, updatedAt, pin, deliveryStage } =
        useDeliverySummary(order, fetchOrder);

    const showResendReceipButton = React.useMemo(
        () => !!action && order?.scenario !== OrderScenario.PAYATPOS,
        [action, order?.scenario]
    );

    return (
        <Box display="flex" flexDirection="column">
            <StyledAppBarContainer isDesktop={isDesktop}>
                {!isDesktop && (
                    <IconButton onClick={onClose} size="large">
                        <ArrowBackIcon />
                    </IconButton>
                )}

                <StyledAppBarTitle isDesktop={isDesktop}>
                    {t('ORDER_TITLE', { orderNumber: orderId })}
                </StyledAppBarTitle>
            </StyledAppBarContainer>
            <StyledOrderDetails>
                <Box width="100%">
                    {showDelivery && (
                        <Paper variant="outlined" sx={{ mb: 2 }}>
                            <DeliverySuccessBox
                                due={due}
                                updatedAt={updatedAt}
                                statusString={statusString}
                                stage={deliveryStage}
                            />
                        </Paper>
                    )}
                    {showPin && (
                        <Paper variant="outlined" sx={{ mb: 2 }}>
                            <Box p={1.5} pt={0} pb={1}>
                                <DeliveryPinBox pin={pin} />
                            </Box>
                        </Paper>
                    )}
                    {location && !locationLoading && (
                        <Paper className={classes.cardImage} variant="outlined">
                            {location && location?.mainImageUrl && (
                                <LoadingImage src={location.mainImageUrl} height={22.5} />
                            )}
                            <Box padding={1.5}>
                                <Typography className={classes.bold} variant="body1" align="center">
                                    {location.title}
                                </Typography>
                                <Typography variant="body1" align="center">
                                    {orderData}
                                </Typography>
                            </Box>
                        </Paper>
                    )}

                    {order && !orderLoading && (
                        <>
                            <Paper className={classes.cardOrderDetails} variant="outlined">
                                <Box marginBottom={1}>
                                    <Typography className={classes.title} variant="h6" align="center">
                                        {t('ORDER_STATUS_ORDER_DETAILS')}
                                    </Typography>
                                </Box>
                                {order?.items && order.items.map(renderItems)}
                            </Paper>

                            <Paper className={classes.cardPayments} variant="outlined">
                                <Box marginBottom={1}>
                                    <Typography className={classes.title} variant="h6" align="center">
                                        {t('ONBOARDING_PAYMENTS_TITLE')}
                                    </Typography>
                                </Box>

                                {order.total.discounts !== 0 && (
                                    <Box
                                        marginBottom={1}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography>{t('PAYMENT_ITEM_REWARDS')}</Typography>
                                        <Typography>
                                            {getCurrencyString(order.total.discounts, order.currencyCode)}
                                        </Typography>
                                    </Box>
                                )}

                                {order.total.charges > 0 && (
                                    <Box
                                        marginBottom={1}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography>{t('PAYMENT_ITEM_CHARGES')}</Typography>
                                        <Typography>
                                            {getCurrencyString(order.total.charges, order.currencyCode)}
                                        </Typography>
                                    </Box>
                                )}

                                {order.total.tips > 0 && (
                                    <Box
                                        marginBottom={1}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Typography>{t('PAYMENT_ITEM_TIP')}</Typography>
                                        <Typography>
                                            {getCurrencyString(order.total.tips, order.currencyCode)}
                                        </Typography>
                                    </Box>
                                )}

                                {order.total.taxes &&
                                    order.total.taxes.length > 0 &&
                                    processTaxes(order.total.taxes) > 0 && (
                                        <Box
                                            marginBottom={1}
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems="center"
                                        >
                                            <Typography>{t('PAYMENT_ITEM_VAT_INCLUDED')}</Typography>
                                            <Typography>
                                                {getCurrencyString(
                                                    processTaxes(order.total.taxes),
                                                    order.currencyCode
                                                )}
                                            </Typography>
                                        </Box>
                                    )}

                                <Box
                                    marginBottom={1}
                                    display="flex"
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Typography>{t('CHECKOUT_TOTAL')}</Typography>
                                    <Typography>{totalSum}</Typography>
                                </Box>
                            </Paper>
                        </>
                    )}

                    {locationLoading && (
                        <Paper className={classes.cardImage} variant="outlined">
                            <Skeleton width="100%" height={180} variant="rectangular" />
                            <Box padding={1.5} display="flex" alignItems="center" flexDirection="column">
                                <Skeleton height={26} width="50%" />
                                <Skeleton height={24} width="75%" />
                            </Box>
                        </Paper>
                    )}

                    {orderLoading && (
                        <>
                            <Paper className={classes.cardOrderDetails} variant="outlined">
                                <Box marginBottom={1}>
                                    <Typography className={classes.title} variant="h6" align="center">
                                        {t('ORDER_STATUS_ORDER_DETAILS')}
                                    </Typography>
                                </Box>
                                {Array(4).fill('').map(renderItemsSkeleton)}
                            </Paper>

                            <Paper className={classes.cardPayments} variant="outlined">
                                <Box marginBottom={1}>
                                    <Typography className={classes.title} variant="h6" align="center">
                                        {t('ONBOARDING_PAYMENTS_TITLE')}
                                    </Typography>
                                </Box>
                                {Array(3).fill('').map(renderPaymentsSkeleton)}
                            </Paper>
                        </>
                    )}
                </Box>
            </StyledOrderDetails>
            {showResendReceipButton && !trackMyOrderLink && (
                <Box height="50px" width="100%">
                    <Box position="fixed" bottom={0} left={0} right={0} p={1}>
                        <ButtonV2
                            color="primary"
                            variant="contained"
                            fullWidth
                            disabled={orderLoading}
                            onClick={handleClickOpenResendReceipt}
                        >
                            {'Resend Receipt'}
                        </ButtonV2>
                    </Box>
                </Box>
            )}
            {trackMyOrderLink && (
                <Box height={showResendReceipButton ? '100px' : '50px'} width="100%">
                    <Box position="fixed" bottom={0} left={0} right={0} p={1}>
                        {showResendReceipButton && (
                            <ButtonV2
                                color="primary"
                                variant="outlined"
                                fullWidth
                                disabled={orderLoading}
                                onClick={handleClickOpenResendReceipt}
                            >
                                {'Resend Receipt'}
                            </ButtonV2>
                        )}
                        <a href={trackMyOrderLink} target="_blank" rel="noreferrer">
                            <ButtonV2 color="primary">{t('DELIVERY_TRACK_ORDER')}</ButtonV2>
                        </a>
                    </Box>
                </Box>
            )}
        </Box>
    );
};
